/**
 * 表格自适应高度 Mixin V2
 * 
 * 使用方法:
 * 1. 在组件中引入: import TableAutoHeightV2 from '@/mixins/table-auto-height-v2'
 * 2. 在 mixins 中使用: mixins: [TableAutoHeightV2]
 * 3. 在 vxe-grid 上绑定高度: :height="tableHeight"
 * 4. 可选配置项:
 *    - minTableHeight: 最小表格高度,默认 260
 *    - safetyMargin: 安全边距,默认 55
 *    - tableRefName: 表格 ref 名称,默认 'vxeGrid'
 * 
 * 示例:
 * export default {
 *   mixins: [TableAutoHeightV2],
 *   data() {
 *     return {
 *       minTableHeight: 300,  // 可选,自定义最小高度
 *       safetyMargin: 60,     // 可选,自定义安全边距
 *     }
 *   }
 * }
 */

export default {
  data() {
    return {
      tableHeight: this.minTableHeight || 260,
      _resizeTimer: null, // 防抖定时器
    }
  },
  
  computed: {
    // 最小表格高度,可在组件中覆盖
    _minTableHeight() {
      return this.minTableHeight || 260
    },
    
    // 安全边距,可在组件中覆盖
    _safetyMargin() {
      return this.safetyMargin || 55
    },
    
    // 表格 ref 名称,可在组件中覆盖
    _tableRefName() {
      return this.tableRefName || 'vxeGrid'
    }
  },
  
  // 新增：适配 keep-alive 模式下的 Tab 切换
  activated() {
    
    // 组件被激活时，重新添加监听器
    window.addEventListener('resize', this.handleTableResize)
    
    // tab 切换时需要更长的延时，确保 DOM 完全可见
    // 使用 $nextTick + 延时组合，确保 Vue 更新完成且过渡动画结束
    this.$nextTick(() => {
      this._activatedTimer = setTimeout(() => {
        // 不检查 offsetParent，直接强制计算
        this.calculateTableHeight(0, true)
      }, 500) // 增加到 500ms，给 tab 切换动画足够时间
    })
  },

  // 新增：组件失活时清理监听
  deactivated() {
    window.removeEventListener('resize', this.handleTableResize)
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer)
    }
    if (this._activatedTimer) {
      clearTimeout(this._activatedTimer)
    }
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleTableResize)
    // 清理定时器
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer)
    }
    if (this._retryTimer) {
      clearTimeout(this._retryTimer)
    }
    if (this._activatedTimer) {
      clearTimeout(this._activatedTimer)
    }
  },
  
  methods: {
    /**
     * 计算表格高度
     * @param {number} retryCount - 重试次数
     * @param {boolean} forceCalculate - 是否强制计算（跳过 offsetParent 检查）
     */
    calculateTableHeight(retryCount = 0, forceCalculate = false) {
      // 使用 requestAnimationFrame 确保在下一帧渲染前执行
      requestAnimationFrame(() => {
        // 稍微延时，等待可能的布局变化（如侧边栏动画、Keep-Alive 恢复等）完成
        // 增加延时到 300ms 以增强稳定性
        setTimeout(() => {
          this.$nextTick(() => {
            try {
              const vxeGrid = this.$refs[this._tableRefName]
              // 检查组件是否存活
              if (!vxeGrid || !vxeGrid.$el) {
                return
              }

              // 检查可见性 (offsetParent 为 null 通常表示 hidden 或 display:none)
              // 但在 activated 时强制计算，因为可能父元素暂时不可见
              if (!forceCalculate && vxeGrid.$el.offsetParent === null) {
                if (retryCount < 5) {
                   // 如果不可见，尝试延时重试，最多重试 5 次
                   setTimeout(() => this.calculateTableHeight(retryCount + 1, false), 500)
                }
                return
              }
              
              // 1. 获取当前页面可视高度 (优先使用 clientHeight 以排除滚动条高度)
              const clientHeight = document.documentElement.clientHeight || window.innerHeight
              
              // 2. 获取表格顶部距离视口顶部的距离
              const rect = vxeGrid.$el.getBoundingClientRect()
              const tableTop = rect.top
              
              // 3. 如果 tableTop 为 0 或负数，说明元素可能还没有正确渲染
              if (tableTop <= 0 && !forceCalculate) {
                if (retryCount < 3) {
                  setTimeout(() => this.calculateTableHeight(retryCount + 1, false), 300)
                }
                return
              }
              
              // 4. 计算理论可用高度
              // 可用高度 = 窗口高度 - 表格顶部位置 - 安全边距
              let availableHeight = clientHeight - tableTop - this._safetyMargin
              
              // 5. 边界检查
              if (availableHeight > clientHeight - 100) {
                 // 如果计算出的高度比整个屏幕还大，做一个兜底
                 availableHeight = clientHeight - 150
              }

              // 6. 应用高度
              const newHeight = Math.max(availableHeight, this._minTableHeight)
              
              if (Math.abs(this.tableHeight - newHeight) > 1) {
                this.tableHeight = newHeight
              }
            } catch (error) {
              console.warn('表格高度计算失败:', error)
              this.tableHeight = this._minTableHeight
            }
          })
        }, 300) // 增加延时到 300ms 以适应大多数过渡动画
      })
    },
    
    /**
     * 窗口大小变化处理（带防抖）
     */
    handleTableResize() {
      if (this._resizeTimer) {
        clearTimeout(this._resizeTimer)
      }
      
      // 立即计算一次（响应快）
      this.calculateTableHeight()
      
      // 延迟再次计算（确保准确）
      this._resizeTimer = setTimeout(() => {
        this.calculateTableHeight()
      }, 300)
    },
    
    /**
     * 手动触发表格高度重新计算
     */
    recalculateTableHeight() {
      // 外部调用时，往往意味着状态刚变，多给点时间让 DOM 稳定
      setTimeout(() => {
        this.calculateTableHeight(0, true)
      }, 100)
    }
  }
}
